'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, ZoomIn, ZoomOut, Layers, Filter, Search, Home, Building2, Wheat, Factory, AlertTriangle, Zap } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = 'AIzaSyAqWJtrdAkQgNwkLGvG33UrBTed9w3DDA0';

const LIBERIAN_COUNTIES = [
  'Montserrado', 'Nimba', 'Bong', 'Lofa', 'Grand Bassa',
  'Margibi', 'Grand Gedeh', 'Sinoe', 'Grand Kru', 'Maryland',
  'River Cess', 'Gbarpolu', 'Grand Cape Mount', 'River Gee', 'Bomi',
];

interface Parcel {
  id: string;
  address: string;
  county: string;
  status: 'registered' | 'unregistered' | 'disputed';
  owner: string;
  area: string;
  lat: number;
  lng: number;
  taxStatus: 'paid' | 'overdue' | 'exempt';
  landUse: 'residential' | 'commercial' | 'agricultural' | 'industrial';
  hasElectricity: boolean;
  hasWater: boolean;
  encroached: boolean;
}

const PARCELS: Parcel[] = [
  { id: 'LR-MON-0042', address: '14 Broad St, Monrovia', county: 'Montserrado', status: 'registered', owner: 'Kollie Trading Co.', area: '0.34 ha', lat: 6.300, lng: -10.797, taxStatus: 'paid', landUse: 'commercial', hasElectricity: true, hasWater: true, encroached: false },
  { id: 'LR-MON-0117', address: '88 Tubman Blvd, Monrovia', county: 'Montserrado', status: 'registered', owner: 'Mary Pewee', area: '0.12 ha', lat: 6.312, lng: -10.804, taxStatus: 'overdue', landUse: 'residential', hasElectricity: true, hasWater: false, encroached: false },
  { id: 'LR-NIM-0008', address: 'Ganta Market Zone B', county: 'Nimba', status: 'unregistered', owner: 'Unknown', area: '1.20 ha', lat: 7.228, lng: -8.988, taxStatus: 'overdue', landUse: 'commercial', hasElectricity: false, hasWater: false, encroached: true },
  { id: 'LR-BON-0031', address: 'Gbarnga Central Plot 3', county: 'Bong', status: 'registered', owner: 'Bong County Dev. Corp.', area: '2.50 ha', lat: 6.994, lng: -9.472, taxStatus: 'exempt', landUse: 'agricultural', hasElectricity: false, hasWater: true, encroached: false },
  { id: 'LR-LOF-0019', address: 'Voinjama Riverside', county: 'Lofa', status: 'disputed', owner: 'Contested — 2 claimants', area: '0.78 ha', lat: 8.422, lng: -9.748, taxStatus: 'overdue', landUse: 'residential', hasElectricity: false, hasWater: false, encroached: true },
  { id: 'LR-GBS-0055', address: 'Buchanan Port Zone', county: 'Grand Bassa', status: 'registered', owner: 'LPA Holdings', area: '5.10 ha', lat: 5.879, lng: -10.046, taxStatus: 'paid', landUse: 'industrial', hasElectricity: true, hasWater: true, encroached: false },
  { id: 'LR-MAR-0022', address: 'Kakata Industrial Blvd', county: 'Margibi', status: 'unregistered', owner: 'Unknown', area: '0.65 ha', lat: 6.528, lng: -10.348, taxStatus: 'overdue', landUse: 'industrial', hasElectricity: true, hasWater: false, encroached: false },
  { id: 'LR-MRY-0004', address: 'Harper Coastal Strip', county: 'Maryland', status: 'registered', owner: 'Maryland Fisheries Ltd.', area: '3.20 ha', lat: 4.375, lng: -7.716, taxStatus: 'paid', landUse: 'commercial', hasElectricity: false, hasWater: true, encroached: false },
  { id: 'LR-GGH-0011', address: 'Zwedru Central Market', county: 'Grand Gedeh', status: 'registered', owner: 'Gedeh Commerce Ltd.', area: '1.80 ha', lat: 5.836, lng: -8.133, taxStatus: 'paid', landUse: 'commercial', hasElectricity: true, hasWater: true, encroached: false },
  { id: 'LR-SIN-0007', address: 'Greenville Port Area', county: 'Sinoe', status: 'unregistered', owner: 'Unknown', area: '2.10 ha', lat: 5.011, lng: -9.039, taxStatus: 'overdue', landUse: 'industrial', hasElectricity: false, hasWater: false, encroached: true },
  // Additional unregistered parcels
  { id: 'LR-MON-0203', address: 'Congo Town Backstreet', county: 'Montserrado', status: 'unregistered', owner: 'Unknown', area: '0.22 ha', lat: 6.318, lng: -10.812, taxStatus: 'overdue', landUse: 'residential', hasElectricity: false, hasWater: false, encroached: false },
  { id: 'LR-MON-0211', address: 'Sinkor 5th Street Plot', county: 'Montserrado', status: 'unregistered', owner: 'Unknown', area: '0.18 ha', lat: 6.308, lng: -10.789, taxStatus: 'overdue', landUse: 'residential', hasElectricity: false, hasWater: false, encroached: true },
  { id: 'LR-MON-0225', address: 'Paynesville Market Fringe', county: 'Montserrado', status: 'unregistered', owner: 'Unknown', area: '0.45 ha', lat: 6.325, lng: -10.762, taxStatus: 'overdue', landUse: 'commercial', hasElectricity: false, hasWater: false, encroached: false },
  { id: 'LR-MAR-0038', address: 'Harbel Rubber Farm Edge', county: 'Margibi', status: 'unregistered', owner: 'Unknown', area: '3.80 ha', lat: 6.271, lng: -10.358, taxStatus: 'overdue', landUse: 'agricultural', hasElectricity: false, hasWater: false, encroached: true },
  { id: 'LR-MAR-0044', address: 'Kakata North Fringe', county: 'Margibi', status: 'unregistered', owner: 'Unknown', area: '1.10 ha', lat: 6.541, lng: -10.362, taxStatus: 'overdue', landUse: 'residential', hasElectricity: false, hasWater: false, encroached: false },
  { id: 'LR-GBS-0071', address: 'Buchanan Inland Strip', county: 'Grand Bassa', status: 'unregistered', owner: 'Unknown', area: '2.30 ha', lat: 5.902, lng: -10.021, taxStatus: 'overdue', landUse: 'agricultural', hasElectricity: false, hasWater: false, encroached: false },
  { id: 'LR-BON-0048', address: 'Suakoko Village Outskirts', county: 'Bong', status: 'unregistered', owner: 'Unknown', area: '4.50 ha', lat: 6.982, lng: -9.511, taxStatus: 'overdue', landUse: 'agricultural', hasElectricity: false, hasWater: false, encroached: true },
  { id: 'LR-NIM-0021', address: 'Sanniquellie Periphery', county: 'Nimba', status: 'unregistered', owner: 'Unknown', area: '1.75 ha', lat: 7.362, lng: -8.706, taxStatus: 'overdue', landUse: 'residential', hasElectricity: false, hasWater: false, encroached: false },
  { id: 'LR-LOF-0033', address: 'Foya Border Zone', county: 'Lofa', status: 'unregistered', owner: 'Unknown', area: '2.90 ha', lat: 8.411, lng: -10.195, taxStatus: 'overdue', landUse: 'commercial', hasElectricity: false, hasWater: false, encroached: true },
  { id: 'LR-GCM-0014', address: 'Robertsport Coastal Plot', county: 'Grand Cape Mount', status: 'unregistered', owner: 'Unknown', area: '1.40 ha', lat: 6.754, lng: -11.369, taxStatus: 'overdue', landUse: 'residential', hasElectricity: false, hasWater: false, encroached: false },
  { id: 'LR-RVC-0009', address: 'Cestos City Fringe', county: 'River Cess', status: 'unregistered', owner: 'Unknown', area: '0.95 ha', lat: 5.478, lng: -9.601, taxStatus: 'overdue', landUse: 'agricultural', hasElectricity: false, hasWater: false, encroached: false },
  { id: 'LR-GBP-0006', address: 'Bopolu Outskirts', county: 'Gbarpolu', status: 'unregistered', owner: 'Unknown', area: '3.20 ha', lat: 7.073, lng: -10.487, taxStatus: 'overdue', landUse: 'agricultural', hasElectricity: false, hasWater: false, encroached: true },
];

const statusConfig = {
  registered: { color: '#34d399', bg: 'rgba(52,211,153,0.15)', label: 'Registered' },
  unregistered: { color: '#22c55e', bg: 'rgba(34,197,94,0.18)', label: 'Unregistered' },
  disputed: { color: '#fbbf24', bg: 'rgba(251,191,36,0.15)', label: 'Disputed' },
};

const taxConfig = {
  paid: { color: '#34d399', label: 'Tax Paid' },
  overdue: { color: '#f87171', label: 'Overdue' },
  exempt: { color: '#94a3b8', label: 'Exempt' },
};

const landUseConfig = {
  residential: { color: '#60a5fa', icon: Home, label: 'Residential' },
  commercial: { color: '#f59e0b', icon: Building2, label: 'Commercial' },
  agricultural: { color: '#34d399', icon: Wheat, label: 'Agricultural' },
  industrial: { color: '#a78bfa', icon: Factory, label: 'Industrial' },
};

interface PropertyMapPanelProps {
  selectedParcelId: string | null;
  onSelectParcel: (id: string) => void;
}

interface LayerState {
  landUse: boolean;
  disputed: boolean;
  utility: boolean;
}

declare global {
  interface Window {
    google: any;
    __googleMapsLoading?: Promise<void>;
  }
}

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject('SSR');

  // Already loaded
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  // Already loading — return same promise
  if (window.__googleMapsLoading) {
    return window.__googleMapsLoading;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const callbackName = '__googleMapsReady_' + Date.now();
    (window as any)[callbackName] = () => {
      resolve();
      delete (window as any)[callbackName];
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      reject(new Error('Google Maps failed to load'));
      delete (window as any)[callbackName];
      window.__googleMapsLoading = undefined;
    };
    document.head.appendChild(script);
  });

  window.__googleMapsLoading = promise;
  return promise;
}

export default function PropertyMapPanel({ selectedParcelId, onSelectParcel }: PropertyMapPanelProps) {
  const [selectedCounty, setSelectedCounty] = useState<string>('All Counties');
  const [searchQuery, setSearchQuery] = useState('');
  const [layers, setLayers] = useState<LayerState>({ landUse: false, disputed: true, utility: false });
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<Map<string, any>>(new Map());

  const filtered = PARCELS.filter((p) => {
    const matchCounty = selectedCounty === 'All Counties' || p.county === selectedCounty;
    const matchSearch = !searchQuery || p.id.toLowerCase().includes(searchQuery.toLowerCase()) || p.address.toLowerCase().includes(searchQuery.toLowerCase()) || p.owner.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCounty && matchSearch;
  });

  const selected = PARCELS.find((p) => p.id === selectedParcelId);

  const toggleLayer = (layer: keyof LayerState) => {
    setLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
  };

  const createMarkerIcon = useCallback((parcel: Parcel, isSelected: boolean) => {
    if (!window.google) return null;
    const cfg = statusConfig[parcel.status];
    const size = isSelected ? 20 : 14;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: cfg.color,
      fillOpacity: 1,
      strokeColor: 'rgba(255,255,255,0.8)',
      strokeWeight: 2,
      scale: size / 2,
    };
  }, []);

  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 6.3156, lng: -10.8074 },
      zoom: 8,
      mapTypeId: 'satellite',
      disableDefaultUI: true,
      zoomControl: false,
    });

    mapInstanceRef.current = map;

    PARCELS.forEach((parcel) => {
      const isSelected = parcel.id === selectedParcelId;
      const marker = new window.google.maps.Marker({
        position: { lat: parcel.lat, lng: parcel.lng },
        map,
        icon: createMarkerIcon(parcel, isSelected),
        title: parcel.id,
      });

      marker.addListener('click', () => {
        onSelectParcel(parcel.id);
      });

      markersRef.current.set(parcel.id, marker);
    });

    setMapReady(true);
  }, [createMarkerIcon, onSelectParcel, selectedParcelId]);

  useEffect(() => {
    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(() => {
        initMap();
      })
      .catch((err) => {
        console.error('Google Maps load error:', err);
        setMapError('Google Maps failed to load. Please check your API key or network connection.');
      });
  }, [initMap]);

  // Update marker icons when selection changes
  useEffect(() => {
    if (!mapReady || !window.google) return;
    markersRef.current.forEach((marker, id) => {
      const parcel = PARCELS.find((p) => p.id === id);
      if (!parcel) return;
      marker.setIcon(createMarkerIcon(parcel, id === selectedParcelId));
    });
    // Pan to selected parcel
    if (selectedParcelId && mapInstanceRef.current) {
      const parcel = PARCELS.find((p) => p.id === selectedParcelId);
      if (parcel) {
        mapInstanceRef.current.panTo({ lat: parcel.lat, lng: parcel.lng });
      }
    }
  }, [selectedParcelId, mapReady, createMarkerIcon]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Map area */}
      <div className="lg:col-span-2 rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        {/* Map toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <div className="flex items-center gap-2">
            <Layers size={15} style={{ color: 'var(--accent)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Liberia — Google Satellite Property Grid</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current?.getZoom() || 7) + 1)}
              className="p-1.5 rounded-md hover:bg-white/5 transition-colors"
              style={{ color: 'var(--dark-muted)' }}
            >
              <ZoomIn size={14} />
            </button>
            <button
              onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current?.getZoom() || 7) - 1)}
              className="p-1.5 rounded-md hover:bg-white/5 transition-colors"
              style={{ color: 'var(--dark-muted)' }}
            >
              <ZoomOut size={14} />
            </button>
          </div>
        </div>

        {/* GIS Layer toggles */}
        <div className="flex items-center gap-2 px-4 py-2 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <span className="text-xs font-medium mr-1" style={{ color: 'var(--dark-muted)' }}>Layers:</span>
          {[
            { key: 'landUse' as keyof LayerState, label: 'Land Use', color: '#60a5fa', icon: Home },
            { key: 'disputed' as keyof LayerState, label: 'Disputes', color: '#fbbf24', icon: AlertTriangle },
            { key: 'utility' as keyof LayerState, label: 'Utilities', color: '#34d399', icon: Zap },
          ].map(({ key, label, color, icon }) => {
            const LayerIcon = icon;
            return (
              <button
                key={key}
                onClick={() => toggleLayer(key)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                style={{
                  backgroundColor: layers[key] ? `${color}20` : 'var(--dark-elevated)',
                  color: layers[key] ? color : 'var(--dark-muted)',
                  border: `1px solid ${layers[key] ? color + '60' : 'var(--dark-border)'}`,
                }}
              >
                <LayerIcon size={11} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Google Maps Container */}
        <div className="relative flex-1" style={{ minHeight: '400px' }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: '400px' }} />

          {/* Loading state */}
          {!mapReady && !mapError && (
            <div className="absolute inset-0 flex items-center justify-center z-10"
              style={{ backgroundColor: 'var(--dark-elevated)' }}>
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Loading Google Satellite Map…</span>
              </div>
            </div>
          )}

          {/* Error state */}
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center z-10"
              style={{ backgroundColor: 'var(--dark-elevated)' }}>
              <div className="flex flex-col items-center gap-3 max-w-xs text-center px-4">
                <AlertTriangle size={32} style={{ color: '#f87171' }} />
                <span className="text-sm font-semibold" style={{ color: '#f87171' }}>Map Load Error</span>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{mapError}</span>
              </div>
            </div>
          )}

          {/* Land Use overlay badges on selected parcel */}
          {layers.landUse && selected && (
            <div className="absolute top-3 left-3 flex flex-col gap-1 pointer-events-none z-[1000]">
              {(() => {
                const lu = landUseConfig[selected.landUse];
                const LUIcon = lu.icon;
                return (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${lu.color}20`, color: lu.color, border: `1px solid ${lu.color}40` }}>
                    <LUIcon size={11} />
                    {lu.label}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Utility overlay info */}
          {layers.utility && selected && (
            <div className="absolute bottom-16 left-3 p-2 rounded-lg text-xs z-[1000]"
              style={{ backgroundColor: 'rgba(7,14,31,0.9)', border: '1px solid var(--dark-border)' }}>
              <p className="font-semibold mb-1" style={{ color: 'var(--dark-text)' }}>Utility Coverage</p>
              <div className="flex items-center gap-1.5 mb-0.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selected.hasElectricity ? '#fbbf24' : '#374151' }} />
                <span style={{ color: selected.hasElectricity ? '#fbbf24' : 'var(--dark-muted)' }}>
                  Electricity {selected.hasElectricity ? '✓' : '✗'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selected.hasWater ? '#60a5fa' : '#374151' }} />
                <span style={{ color: selected.hasWater ? '#60a5fa' : 'var(--dark-muted)' }}>
                  Water {selected.hasWater ? '✓' : '✗'}
                </span>
              </div>
            </div>
          )}

          {/* Disputed flag */}
          {layers.disputed && selected?.encroached && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold z-[1000]"
              style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.4)' }}>
              <AlertTriangle size={12} />
              Boundary Conflict Flagged
            </div>
          )}

          {/* Legend */}
          <div className="absolute bottom-3 left-3 flex flex-col gap-1.5 p-2 rounded-lg z-[1000]"
            style={{ backgroundColor: 'rgba(7,14,31,0.85)', border: '1px solid var(--dark-border)' }}>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color }} />
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{cfg.label}</span>
              </div>
            ))}
          </div>

          {/* Selected parcel tooltip */}
          {selected && (
            <div className="absolute bottom-3 right-3 p-3 rounded-lg max-w-xs z-[1000]"
              style={{ backgroundColor: 'rgba(7,14,31,0.92)', border: '1px solid var(--dark-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={13} style={{ color: 'var(--accent)' }} />
                <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{selected.id}</span>
              </div>
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--dark-text)' }}>{selected.address}</p>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{selected.owner}</p>
              <p className="text-xs mb-2" style={{ color: 'var(--dark-muted)' }}>{selected.county} · {selected.area}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: statusConfig[selected.status].bg, color: statusConfig[selected.status].color }}>
                  {statusConfig[selected.status].label}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: taxConfig[selected.taxStatus].color }}>
                  {taxConfig[selected.taxStatus].label}
                </span>
                {layers.landUse && (
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${landUseConfig[selected.landUse].color}20`, color: landUseConfig[selected.landUse].color }}>
                    {landUseConfig[selected.landUse].label}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Parcel list sidebar */}
      <div className="rounded-xl flex flex-col" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--dark-text)' }}>Parcel Filter</p>
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
            <input
              type="text"
              placeholder="Search ID, address, owner…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs outline-none"
              style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
            />
          </div>
          <div className="relative">
            <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
            <select
              value={selectedCounty}
              onChange={(e) => setSelectedCounty(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs outline-none appearance-none"
              style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
            >
              <option>All Counties</option>
              {LIBERIAN_COUNTIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1.5" style={{ maxHeight: '460px' }}>
          {filtered.map((parcel) => {
            const cfg = statusConfig[parcel.status];
            const isSelected = parcel.id === selectedParcelId;
            const lu = landUseConfig[parcel.landUse];
            return (
              <button
                key={parcel.id}
                onClick={() => {
                  onSelectParcel(parcel.id);
                  if (mapInstanceRef.current) {
                    mapInstanceRef.current.setCenter({ lat: parcel.lat, lng: parcel.lng });
                    mapInstanceRef.current.setZoom(14);
                  }
                }}
                className="w-full text-left p-2.5 rounded-lg transition-all duration-150"
                style={{
                  backgroundColor: isSelected ? 'rgba(212,160,23,0.1)' : 'var(--dark-elevated)',
                  border: `1px solid ${isSelected ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{parcel.id}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                </div>
                <p className="text-xs truncate" style={{ color: 'var(--dark-text)' }}>{parcel.address}</p>
                <p className="text-xs truncate mt-0.5" style={{ color: 'var(--dark-muted)' }}>{parcel.owner} · {parcel.area}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${lu.color}15`, color: lu.color }}>
                    {lu.label}
                  </span>
                  {parcel.encroached && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>
                      ⚠ Dispute
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
